import { useState } from 'react';
import { useJobPostsStore } from '@/store/useJobPostsStore';
import { JobPost } from '@/types/jobPost.types';
import {
  Box,
  Button,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  Textarea,
  TagsInput,
  ActionIcon,
  Modal,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconEdit, IconTrash, IconPlus, IconSparkles } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

interface JobPostFormData extends Omit<JobPost, 'id' | 'createdAt' | 'updatedAt'> { }

const initialFormData: JobPostFormData = {
  title: '',
  company: '',
  website: '',
  requiredSkills: [],
  preferredSkills: [],
  description: '',
};

export default function JobPostsPage() {
  const { jobPosts, addJobPost, updateJobPost, deleteJobPost } = useJobPostsStore();
  const [opened, { open, close }] = useDisclosure(false);
  const [formData, setFormData] = useState<JobPostFormData>(initialFormData);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleSubmit = () => {
    if (editingId) {
      updateJobPost({ ...formData, id: editingId } as JobPost);
    } else {
      addJobPost(formData);
    }
    handleClose();
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setEditingId(null);
    close();
  };

  const handleEdit = (jobPost: JobPost) => {
    const { id, createdAt, updatedAt, ...rest } = jobPost;
    setFormData(rest);
    setEditingId(id);
    open();
  };

  const openDeleteModal = (post: JobPost) => {
    modals.openConfirmModal({
      title: 'Delete Job Post',
      centered: true,
      children: (
        <Text size="sm">
          Are you sure you want to delete the job post "{post.title}" at {post.company}?
          This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteJobPost(post.id),
    });
  };

  return (
    <Box p="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>{t('job_posts')}</Title>
        <Group>
          <Tooltip label={t('smart_paste')}>
            <Button>
              <IconSparkles size={24} />
            </Button>
          </Tooltip>
          <Button leftSection={<IconPlus size={16} />} onClick={() => open()}>
            {t('create_job_post')}
          </Button>
        </Group>
      </Group>

      <Stack gap="md">
        {jobPosts.map((post) => (
          <Card key={post.id} withBorder>
            <Group justify="space-between" mb="xs">
              <Text fw={500} size="lg">{post.title}</Text>
              <Group gap="xs">
                <ActionIcon
                  variant="subtle"
                  color="blue"
                  onClick={() => handleEdit(post)}
                >
                  <IconEdit size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => openDeleteModal(post)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Group>
            <Text c="dimmed" size="sm" mb="xs">{post.company}</Text>
            <Text size="sm" mb="xs">
              <Text span fw={500}>Website:</Text> {post.website}
            </Text>
            <Text size="sm" mb="xs">
              <Text span fw={500}>Required Skills:</Text>{' '}
              {post.requiredSkills.join(', ')}
            </Text>
            <Text size="sm" mb="xs">
              <Text span fw={500}>Preferred Skills:</Text>{' '}
              {post.preferredSkills.join(', ')}
            </Text>
            <Text size="sm">{post.description}</Text>
          </Card>
        ))}
      </Stack>

      <Modal
        opened={opened}
        onClose={handleClose}
        title={editingId ? 'Edit Job Post' : 'Add Job Post'}
        size="lg"
      >
        <Stack>
          <TextInput
            label="Title"
            placeholder="Enter job title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <TextInput
            label="Company"
            placeholder="Enter company name"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
          />
          <TextInput
            label="Website"
            placeholder="Enter job posting website"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
          />
          <TagsInput
            label="Required Skills"
            placeholder="Enter required skills"
            value={formData.requiredSkills}
            onChange={(value) => setFormData({ ...formData, requiredSkills: value })}
          />
          <TagsInput
            label="Preferred Skills"
            placeholder="Enter preferred skills"
            value={formData.preferredSkills}
            onChange={(value) => setFormData({ ...formData, preferredSkills: value })}
          />
          <Textarea
            label="Description"
            placeholder="Enter job description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            minRows={4}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSubmit}>
              {editingId ? 'Update' : 'Add'} Job Post
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
